import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QuantityError, suggestExactAlternatives, toBaseQuantity } from '@wms/shared';
import { useTranslation } from 'react-i18next';
import { ApiError, apiFetch } from '../../lib/api';
import { getCurrentWarehouseId } from '../../lib/warehouse';
import { LocationTreeSelect } from '../location/location-tree-select';
import type { LocationNode } from '../location/types';
import { PackagingUnitFields } from '../product/packaging-unit-fields';
import { ExpiryBadge } from './expiry-badge';
import type { InboundResponse, MergeCandidate } from './types';
import type { ProductDetail, ProductListItem, SupplierItem } from '../product/types';

interface FormState {
  productId: string | null;
  qty: string;
  unitId: string; // uom id
  externalLotNo: string;
  expiryMode: 'DATE' | 'NONE';
  expirationDate: string;
  manufacturingDate: string;
  mergeBatchId: string | null; // null = 新建批次
  locationId: string | null;
  acknowledgeNonStorage: boolean;
  supplierId: string;
  costAmount: string;
  costCurrency: string;
  note: string;
}

const emptyForm = (): FormState => ({
  productId: null,
  qty: '',
  unitId: '',
  externalLotNo: '',
  expiryMode: 'DATE',
  expirationDate: '',
  manufacturingDate: '',
  mergeBatchId: null,
  locationId: null,
  acknowledgeNonStorage: false,
  supplierId: '',
  costAmount: '',
  costCurrency: 'CNY',
  note: '',
});

/**
 * 入库抽屉（F2 / Spec §18）：单列步进式。产品 → 数量与单位（实时换算，D-05）
 * → 批次与效期（§14 二选一；同维度并批提示，绝不自动合并）→ 位置（非存储
 * 位置需显式确认）→ 供应商/成本/备注 → 确认。
 *
 * `Idempotency-Key` 在抽屉打开与每次成功后重新生成，重试沿用同一个 key——
 * 网络重试/双击绝不会产生第二笔入库（幂等协议见后端 idempotency.service.ts）。
 */
export function InboundDrawer({ onClose }: { readonly onClose: () => void }): React.JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const warehouseId = getCurrentWarehouseId()!;
  const [form, setForm] = useState<FormState>(emptyForm);
  const [productSearch, setProductSearch] = useState('');
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const [success, setSuccess] = useState<InboundResponse | null>(null);

  const patch = (p: Partial<FormState>): void => setForm((f) => ({ ...f, ...p }));

  // —— ① 产品搜索与选择 ——
  const { data: productOptions } = useQuery({
    queryKey: ['inbound', 'product-search', productSearch],
    queryFn: () =>
      apiFetch<{ items: ProductListItem[] }>(
        `/products?limit=8&query=${encodeURIComponent(productSearch)}`,
      ),
    enabled: productSearch.trim().length > 0 && form.productId === null,
  });

  const { data: product } = useQuery({
    queryKey: ['inbound', 'product', form.productId],
    queryFn: () => apiFetch<ProductDetail>(`/products/${form.productId}`),
    enabled: form.productId !== null,
  });

  const activeUnits = useMemo(() => (product?.units ?? []).filter((u) => !u.archivedAt), [product]);
  const selectedUnit = activeUnits.find((u) => u.uomId === form.unitId);

  // —— 0023：强制单位补全（批量导入没有单位数据的产品，入库前必须先设置） ——
  const [setupBaseUnitId, setSetupBaseUnitId] = useState('');
  const [setupPackagingUomId, setSetupPackagingUomId] = useState('');
  const [setupPackagingQty, setSetupPackagingQty] = useState('');
  const setupQtyValid = /^[1-9]\d*$/.test(setupPackagingQty.trim());
  const completeUnitSetup = useMutation({
    mutationFn: () =>
      apiFetch(`/products/${form.productId}/complete-unit-setup`, {
        method: 'POST',
        body: {
          baseUnitId: setupBaseUnitId,
          ...(setupPackagingUomId && setupQtyValid
            ? { packaging: { uomId: setupPackagingUomId, factorToBase: setupPackagingQty.trim() } }
            : {}),
        },
      }),
    onSuccess: () => {
      setSetupBaseUnitId('');
      setSetupPackagingUomId('');
      setSetupPackagingQty('');
      // 产品详情需要重新拉取——needsUnitSetup 清除后，下面的数量/单位区
      // 才会换成真实单位列表（而不是继续显示已归档的占位单位）
      void queryClient.invalidateQueries({ queryKey: ['inbound', 'product', form.productId] });
    },
  });

  // —— ② 实时换算（与后端同一份 quantity.ts 数学） ——
  // M5-F1 修复：QuantityError 只携带 {input, factor}，与 errors.json 的
  // QUANTITY.NOT_DIVISIBLE 模板所需的 {{inexact}}/{{lower}}/{{upper}}/{{baseUnit}}
  // 不同形——之前只传 err.code 当 defaultValue，插值全部落空，UI 直接渲染裸占位符。
  // suggestExactAlternatives 是专为这个提示准备的（quantity.ts 文档明确写着这个用途），
  // 之前从未被抽屉调用过。这里补全全部插值参数。
  const conversion = useMemo(() => {
    if (!selectedUnit || form.qty.trim() === '') return null;
    try {
      return {
        baseQty: toBaseQuantity(form.qty, BigInt(selectedUnit.factorToBase)),
        error: null,
        errorParams: undefined,
      } as const;
    } catch (err) {
      if (err instanceof QuantityError) {
        const params: Record<string, string> = { input: form.qty, unit: selectedUnit.uomCode };
        if (err.code === 'QUANTITY.NOT_DIVISIBLE') {
          const baseUnit = activeUnits.find((u) => u.isBase);
          const alt = suggestExactAlternatives(form.qty, BigInt(selectedUnit.factorToBase));
          params.inexact = alt.inexactBase;
          params.lower = alt.lowerBase.toString();
          params.upper = alt.upperBase.toString();
          if (baseUnit) params.baseUnit = baseUnit.uomCode;
        }
        return { baseQty: null, error: err.code, errorParams: params } as const;
      }
      throw err;
    }
  }, [form.qty, selectedUnit, activeUnits]);

  // —— ③ 并批候选（产品+外部批号+效期+供应商 四维度全同才提示） ——
  const mergeParams = new URLSearchParams();
  if (form.productId) mergeParams.set('productId', form.productId);
  if (form.externalLotNo.trim()) mergeParams.set('externalLotNo', form.externalLotNo.trim());
  if (form.expiryMode === 'DATE' && form.expirationDate)
    mergeParams.set('expirationDate', form.expirationDate);
  if (form.supplierId) mergeParams.set('supplierId', form.supplierId);
  const { data: mergeData } = useQuery({
    queryKey: ['inbound', 'merge-candidates', mergeParams.toString()],
    queryFn: () =>
      apiFetch<{ items: MergeCandidate[] }>(
        `/inventory/batches/merge-candidates?${mergeParams.toString()}`,
      ),
    enabled:
      form.productId !== null && (form.expiryMode === 'NONE' || form.expirationDate.length > 0),
  });
  const candidates = mergeData?.items ?? [];

  // —— ④ 位置（非存储须确认） ——
  const { data: locations } = useQuery({
    queryKey: ['locations', warehouseId],
    queryFn: () => apiFetch<{ items: LocationNode[] }>('/locations'),
  });
  const selectedLocation = (locations?.items ?? []).find((l) => l.id === form.locationId);
  const nonStorage = selectedLocation ? !selectedLocation.canStoreInventory : false;

  // —— ⑤ 供应商（无 supplier.view 权限时静默隐藏该字段） ——
  const { data: suppliers } = useQuery({
    queryKey: ['inbound', 'suppliers'],
    queryFn: () => apiFetch<{ items: SupplierItem[] }>('/suppliers'),
    retry: false,
  });

  const mutation = useMutation({
    mutationFn: () =>
      apiFetch<InboundResponse>('/inventory/inbound', {
        method: 'POST',
        headers: { 'idempotency-key': idempotencyKey },
        body: {
          productId: form.productId,
          quantity: { value: form.qty.trim(), unitId: form.unitId },
          batch: form.mergeBatchId
            ? { mode: 'EXISTING', batchId: form.mergeBatchId }
            : {
                mode: 'NEW',
                ...(form.externalLotNo.trim() ? { externalLotNo: form.externalLotNo.trim() } : {}),
                ...(form.expiryMode === 'DATE'
                  ? { expirationDate: form.expirationDate }
                  : { noExpiration: true }),
                ...(form.manufacturingDate ? { manufacturingDate: form.manufacturingDate } : {}),
              },
          locationId: form.locationId,
          ...(nonStorage ? { acknowledgeNonStorage: form.acknowledgeNonStorage } : {}),
          ...(form.supplierId ? { supplierId: form.supplierId } : {}),
          ...(form.costAmount.trim()
            ? { unitCost: { amount: form.costAmount.trim(), currency: form.costCurrency } }
            : {}),
          ...(form.note.trim() ? { note: form.note.trim() } : {}),
        },
      }),
    onSuccess: (result) => {
      setSuccess(result);
      void queryClient.invalidateQueries({ queryKey: ['inventory'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      // 并批候选/产品搜索的缓存必须随本次入库作废——否则 15s staleTime 内
      // "再入一笔"同产品同效期时，候选列表还是入库前的空结果，并批提示
      // 静默缺席（实测复现：第二笔 candidates=0，等缓存过期后才是 2）
      void queryClient.invalidateQueries({ queryKey: ['inbound'] });
    },
  });

  const canSubmit =
    form.productId !== null &&
    product?.needsUnitSetup !== true &&
    conversion?.baseQty != null &&
    conversion.baseQty > 0n &&
    form.locationId !== null &&
    (!nonStorage || form.acknowledgeNonStorage) &&
    (form.mergeBatchId !== null || form.expiryMode === 'NONE' || form.expirationDate.length > 0) &&
    !mutation.isPending;

  /** F2 完成后动作："再入一笔"保留供应商与位置，清空产品与数量 */
  const receiveAnother = (): void => {
    setSuccess(null);
    setIdempotencyKey(crypto.randomUUID());
    setProductSearch('');
    setForm((f) => ({
      ...emptyForm(),
      supplierId: f.supplierId,
      costCurrency: f.costCurrency,
      locationId: f.locationId,
      acknowledgeNonStorage: f.acknowledgeNonStorage,
    }));
    mutation.reset();
  };

  return (
    <div className="drawer-overlay" onClick={onClose}>
      <aside className="drawer" onClick={(e) => e.stopPropagation()}>
        <div className="drawer__header">
          <h2 className="drawer__title">{t('inbound.title')}</h2>
          <button className="btn btn--ghost btn--sm" onClick={onClose}>
            ✕
          </button>
        </div>

        {success ? (
          <div style={{ padding: 'var(--space-4)' }}>
            <p className="badge badge--success" style={{ marginBottom: 'var(--space-3)' }}>
              {t('inbound.successTitle')}
            </p>
            <p>
              {t('inbound.successDetail', {
                doc: success.docNo,
                batch: success.batchNo,
                qty: success.qtyBase,
                after: success.locationQtyAfter,
              })}
            </p>
            <div className="row" style={{ gap: 'var(--space-2)', marginTop: 'var(--space-4)' }}>
              <button className="btn btn--primary" onClick={receiveAnother}>
                {t('inbound.receiveAnother')}
              </button>
              <button className="btn btn--ghost" onClick={onClose}>
                {t('app.close')}
              </button>
            </div>
          </div>
        ) : (
          <div style={{ padding: 'var(--space-4)', overflowY: 'auto' }}>
            {/* ① 产品 */}
            <label className="field">
              <span>{t('inbound.stepProduct')}</span>
              {form.productId && product ? (
                <div className="row" style={{ gap: 'var(--space-2)', alignItems: 'center' }}>
                  <strong>{product.name}</strong>
                  <span className="field-hint">{product.sku}</span>
                  <button
                    className="btn btn--ghost btn--sm"
                    onClick={() => {
                      patch({ productId: null, unitId: '', mergeBatchId: null });
                      setProductSearch('');
                    }}
                  >
                    {t('inbound.changeProduct')}
                  </button>
                </div>
              ) : (
                <>
                  <input
                    autoFocus
                    placeholder={t('products.searchPlaceholder')}
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                  />
                  {(productOptions?.items ?? []).map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      className="tree__row"
                      onClick={() => {
                        const base = p.baseUnitCode;
                        patch({ productId: p.id, unitId: '' });
                        void base;
                      }}
                    >
                      <span style={{ flex: 2 }}>{p.name}</span>
                      <span className="field-hint">{p.sku}</span>
                      {p.needsUnitSetup && (
                        <span className="badge badge--warn">{t('products.needsUnitSetupBadge')}</span>
                      )}
                    </button>
                  ))}
                </>
              )}
            </label>

            {/* ② 数量与单位——0023：needsUnitSetup 的产品必须先补完单位才能继续 */}
            {product && product.needsUnitSetup && (
              <div
                className="card"
                style={{ padding: 'var(--space-3)', borderLeft: '3px solid var(--color-warning)' }}
              >
                <p style={{ marginBottom: 'var(--space-2)' }}>{t('inbound.unitSetupRequired')}</p>
                <PackagingUnitFields
                  baseUnitId={setupBaseUnitId}
                  onBaseUnitIdChange={setSetupBaseUnitId}
                  packagingUomId={setupPackagingUomId}
                  onPackagingUomIdChange={setSetupPackagingUomId}
                  packagingQty={setupPackagingQty}
                  onPackagingQtyChange={setSetupPackagingQty}
                />
                {completeUnitSetup.error instanceof ApiError && (
                  <p className="field-error">
                    {t(`errors:${completeUnitSetup.error.code}`, {
                      defaultValue: completeUnitSetup.error.code,
                      ...completeUnitSetup.error.params,
                    })}
                  </p>
                )}
                <button
                  type="button"
                  className="btn btn--primary btn--sm"
                  style={{ marginTop: 'var(--space-2)' }}
                  disabled={!setupBaseUnitId || completeUnitSetup.isPending}
                  onClick={() => completeUnitSetup.mutate()}
                >
                  {completeUnitSetup.isPending ? t('app.loading') : t('inbound.unitSetupConfirm')}
                </button>
              </div>
            )}
            {product && !product.needsUnitSetup && (
              <div className="row" style={{ gap: 'var(--space-2)', alignItems: 'flex-end' }}>
                <label className="field" style={{ maxWidth: '140px' }}>
                  <span>{t('inbound.stepQuantity')}</span>
                  <input
                    inputMode="decimal"
                    value={form.qty}
                    onChange={(e) => patch({ qty: e.target.value })}
                    aria-invalid={conversion?.error != null}
                  />
                </label>
                <label className="field" style={{ maxWidth: '160px' }}>
                  <span>{t('units.unit')}</span>
                  <select value={form.unitId} onChange={(e) => patch({ unitId: e.target.value })}>
                    <option value="">{t('inbound.pickUnit')}</option>
                    {activeUnits.map((u) => (
                      <option key={u.uomId} value={u.uomId}>
                        {u.uomCode}
                      </option>
                    ))}
                  </select>
                </label>
                <div style={{ paddingBottom: 'var(--space-2)' }}>
                  {conversion?.baseQty != null && (
                    <span className="field-hint">
                      {t('units.baseQuantityIs', {
                        value: conversion.baseQty.toString(),
                        unit: activeUnits.find((u) => u.isBase)?.uomCode ?? '',
                      })}
                    </span>
                  )}
                  {conversion?.error && (
                    <span className="field-error">
                      {t(`errors:${conversion.error}`, {
                        defaultValue: conversion.error,
                        ...conversion.errorParams,
                      })}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* ③ 批次与效期（§14 必填二选一） */}
            {product && (
              <fieldset className="field" style={{ border: 'none', padding: 0 }}>
                <span>{t('inbound.stepBatch')}</span>
                <input
                  placeholder={t('inbound.externalLot')}
                  value={form.externalLotNo}
                  onChange={(e) => patch({ externalLotNo: e.target.value, mergeBatchId: null })}
                />
                <div className="row" style={{ gap: 'var(--space-3)', marginTop: 'var(--space-2)' }}>
                  <label className="row" style={{ gap: '4px' }}>
                    <input
                      type="radio"
                      checked={form.expiryMode === 'DATE'}
                      onChange={() => patch({ expiryMode: 'DATE', mergeBatchId: null })}
                    />
                    {t('inbound.hasExpiry')}
                  </label>
                  <label className="row" style={{ gap: '4px' }}>
                    <input
                      type="radio"
                      checked={form.expiryMode === 'NONE'}
                      onChange={() => patch({ expiryMode: 'NONE', mergeBatchId: null })}
                    />
                    {t('status.noExpiration')}
                  </label>
                  {form.expiryMode === 'DATE' && (
                    <input
                      type="date"
                      value={form.expirationDate}
                      onChange={(e) =>
                        patch({ expirationDate: e.target.value, mergeBatchId: null })
                      }
                    />
                  )}
                </div>
                <label className="field" style={{ marginTop: 'var(--space-2)' }}>
                  <span>{t('inbound.manufacturingDate')}</span>
                  <input
                    type="date"
                    value={form.manufacturingDate}
                    onChange={(e) => patch({ manufacturingDate: e.target.value })}
                  />
                </label>

                {candidates.length > 0 && (
                  <div
                    className="card"
                    style={{
                      padding: 'var(--space-3)',
                      marginTop: 'var(--space-2)',
                      borderLeft: '3px solid var(--color-warning)',
                    }}
                  >
                    <p style={{ marginBottom: 'var(--space-2)' }}>{t('inbound.mergeHint')}</p>
                    {candidates.map((c) => (
                      <label key={c.id} className="row" style={{ gap: 'var(--space-2)' }}>
                        <input
                          type="radio"
                          checked={form.mergeBatchId === c.id}
                          onChange={() => patch({ mergeBatchId: c.id })}
                        />
                        <span>{c.batchNo}</span>
                        <span className="field-hint">
                          {t('inbound.candidateQty', { qty: c.qtyOnHandBase })}
                        </span>
                        <ExpiryBadge
                          expirationDate={c.expirationDate}
                          hasExpiration={c.expirationDate !== null}
                        />
                      </label>
                    ))}
                    <label className="row" style={{ gap: 'var(--space-2)' }}>
                      <input
                        type="radio"
                        checked={form.mergeBatchId === null}
                        onChange={() => patch({ mergeBatchId: null })}
                      />
                      <span>{t('inbound.createNewBatch')}</span>
                    </label>
                  </div>
                )}
              </fieldset>
            )}

            {/* ④ 位置 */}
            {product && (
              <div className="field">
                <span>{t('inbound.stepLocation')}</span>
                <LocationTreeSelect
                  warehouseId={warehouseId}
                  value={form.locationId}
                  onChange={(id) => patch({ locationId: id, acknowledgeNonStorage: false })}
                  allowRoot={false}
                />
                {nonStorage && (
                  <div
                    className="card"
                    style={{
                      padding: 'var(--space-2)',
                      borderLeft: '3px solid var(--color-warning)',
                    }}
                  >
                    <p>{t('inbound.nonStorageWarning')}</p>
                    <label className="row" style={{ gap: '4px' }}>
                      <input
                        type="checkbox"
                        checked={form.acknowledgeNonStorage}
                        onChange={(e) => patch({ acknowledgeNonStorage: e.target.checked })}
                      />
                      {t('inbound.nonStorageAcknowledge')}
                    </label>
                  </div>
                )}
              </div>
            )}

            {/* ⑤ 供应商 / 成本 / 备注 */}
            {product && (
              <>
                {suppliers && (
                  <label className="field">
                    <span>{t('inbound.supplier')}</span>
                    <select
                      value={form.supplierId}
                      onChange={(e) => patch({ supplierId: e.target.value, mergeBatchId: null })}
                    >
                      <option value="">{t('inbound.noSupplier')}</option>
                      {suppliers.items
                        .filter((s) => !s.archivedAt)
                        .map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name}
                          </option>
                        ))}
                    </select>
                  </label>
                )}
                <div className="row" style={{ gap: 'var(--space-2)' }}>
                  <label className="field" style={{ maxWidth: '140px' }}>
                    <span>{t('inbound.unitCost')}</span>
                    <input
                      inputMode="decimal"
                      placeholder="0.00"
                      value={form.costAmount}
                      onChange={(e) => patch({ costAmount: e.target.value })}
                    />
                  </label>
                  <label className="field" style={{ maxWidth: '100px' }}>
                    <span>{t('inbound.currency')}</span>
                    <input
                      maxLength={3}
                      value={form.costCurrency}
                      onChange={(e) => patch({ costCurrency: e.target.value.toUpperCase() })}
                    />
                  </label>
                </div>
                <label className="field">
                  <span>{t('inbound.note')}</span>
                  <input value={form.note} onChange={(e) => patch({ note: e.target.value })} />
                </label>
              </>
            )}

            {mutation.error instanceof ApiError && (
              <p className="field-error">
                {t(`errors:${mutation.error.code}`, {
                  defaultValue: mutation.error.code,
                  ...mutation.error.params,
                })}
              </p>
            )}

            <div className="row row--between" style={{ marginTop: 'var(--space-4)' }}>
              <span className="field-hint">
                {conversion?.baseQty != null && selectedLocation
                  ? t('inbound.previewLine', {
                      qty: conversion.baseQty.toString(),
                      unit: activeUnits.find((u) => u.isBase)?.uomCode ?? '',
                      location: selectedLocation.name,
                    })
                  : ''}
              </span>
              <div className="row" style={{ gap: 'var(--space-2)' }}>
                <button className="btn btn--ghost" onClick={onClose}>
                  {t('app.cancel')}
                </button>
                <button
                  className="btn btn--primary"
                  disabled={!canSubmit}
                  onClick={() => mutation.mutate()}
                >
                  {mutation.isPending ? t('app.loading') : t('inbound.confirm')}
                </button>
              </div>
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}
